/**
 * Route-id → assistant pageId mapping, DERIVED from the shared
 * `ASSISTANT_PAGES` vocabulary (packages/types/src/assistant-pages.ts) —
 * adding a page there wires this mapping automatically; nothing to edit here.
 *
 * The router computes route identity and typed params authoritatively; this
 * resolves them onto the pageId vocabulary the API's capability registry is
 * keyed by. Unmapped routes degrade to 'unknown' (global capabilities only) —
 * never an error.
 */

import { ASSISTANT_PAGES, assistantPageLabel, type AssistantPageId } from '@chatsdet/types';

export type PageRegistryEntry = {
  pageId: AssistantPageId;
  /** Route params to lift off the match, named in ASSISTANT_PAGES. */
  entityParams: readonly string[];
};

/** Keyed by TanStack route id (routes are flat children of root → id = path). */
export const PAGE_REGISTRY: Record<string, PageRegistryEntry> = Object.fromEntries(
  ASSISTANT_PAGES.flatMap((page) =>
    page.routeIds.map((routeId) => [
      routeId,
      { pageId: page.id, entityParams: page.entityParams },
    ])
  )
);

export type ResolvedPageContext = {
  pageId: AssistantPageId;
  label: string;
  pathname: string;
  params: Record<string, string>;
};

/**
 * Resolve the assistant page context from the router's match list.
 * Walks matches deepest-first and returns the first registered one.
 */
export function resolvePageContext(
  matches: Array<{ routeId: string; params: Record<string, unknown> }>,
  pathname: string,
  search: Record<string, unknown>
): ResolvedPageContext {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i]!;
    // Route ids may carry a trailing slash for index routes ('/runs/').
    const routeId = match.routeId.length > 1 ? match.routeId.replace(/\/$/, '') : match.routeId;
    const entry = PAGE_REGISTRY[routeId];
    if (!entry) continue;

    const params: Record<string, string> = {};
    for (const paramName of entry.entityParams) {
      const value = match.params[paramName];
      if (typeof value === 'string' && value.length > 0) {
        params[paramName] = value;
        continue;
      }
      // Some entity params live only in the URL's search string, not the
      // route's path params — the flow designer's run view (`?runId=`) and
      // the product map's selected application (`?propertyId=`) both work
      // this way. Fall back to the search param of the same name.
      const searchValue = search[paramName];
      if (typeof searchValue === 'string' && searchValue.length > 0) {
        params[paramName] = searchValue;
      }
    }

    return { pageId: entry.pageId, label: assistantPageLabel(entry.pageId), pathname, params };
  }

  return { pageId: 'unknown', label: 'Unknown page', pathname, params: {} };
}

export type AssistantLocation = {
  to: string;
  params: Record<string, string>;
  search: Record<string, string>;
};

/**
 * Build a TanStack Router location for the `navigate` tool: the page's first
 * `routeIds` entry as `to`, with `params` supplying every `$name` path
 * segment and `search` carrying entity params the page declares but that
 * aren't part of the path (mirroring `resolvePageContext`'s search fallback).
 *
 * Throws on an unknown/unnavigable pageId or a missing path param — both
 * messages are surfaced back to the model as the tool's error output, so they
 * name the fix (which pageId, which param).
 */
export function buildLocation(pageId: string, params: Record<string, string>): AssistantLocation {
  const page = ASSISTANT_PAGES.find((candidate) => candidate.id === pageId);
  if (!page || page.routeIds.length === 0) {
    // Unreachable through the tool: the server's schema is an enum of exactly
    // the navigable ids. Kept because this is a public function whose contract
    // is "a location or a loud error", not "trust the caller".
    throw new Error(`navigate: "${pageId}" is not a navigable page`);
  }

  const to = page.routeIds[0]!;
  const pathParamNames = new Set(
    Array.from(to.matchAll(/\$([A-Za-z0-9_]+)/g)).map((match) => match[1]!)
  );

  const routeParams: Record<string, string> = {};
  for (const name of pathParamNames) {
    const value = params[name];
    if (!value) {
      throw new Error(`navigate to ${pageId} needs params.${name}`);
    }
    routeParams[name] = value;
  }

  const search: Record<string, string> = {};
  for (const name of page.entityParams) {
    if (pathParamNames.has(name)) continue;
    const value = params[name];
    if (value) search[name] = value;
  }

  return { to, params: routeParams, search };
}
